import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const GOOGLE_SHEET_URL =
  "https://script.google.com/macros/s/AKfycbw6sYTBzHjXLKl7iBPg0VNQ_t3XfK7dzLiiBkdWXrH6gncvk9PN3E8u5LdoW2aiMkuU/exec";

const Contact = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fname: "",
    email: "",
    phone: "",
    date: "",
    type: "",
    location: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^\S+@\S+\.\S+$/;
    const phoneRegex = /^[0-9]{7,15}$/;

    if (!formData.fname.trim()) newErrors.fname = "Full name is required.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Invalid email format.";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required.";
    else if (!phoneRegex.test(formData.phone)) newErrors.phone = "Invalid phone number.";
    if (!formData.date) newErrors.date = "Event date is required.";
    if (!formData.type.trim()) newErrors.type = "Event type is required.";
    if (!formData.location.trim()) newErrors.location = "Event location is required.";
    if (!formData.message.trim()) newErrors.message = "Message is required.";

    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  // ✅ Silent Netlify submission — runs alongside Google Sheets, no page reload
  const submitToNetlify = () => {
    const body = new URLSearchParams({
      "form-name": "contact",
      fname: formData.fname,
      email: formData.email,
      phone: formData.phone,
      date: formData.date,
      type: formData.type,
      location: formData.location,
      message: formData.message,
    });

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    }).catch(() => {
      // Silently fail — Google Sheets is the primary destination
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // ✅ Submit to both Netlify and Google Sheets simultaneously
      submitToNetlify();

      const formBody = new URLSearchParams({
        fullName: formData.fname,
        email: formData.email,
        phone: formData.phone,
        eventDate: formData.date,
        eventType: formData.type,
        eventLocation: formData.location,
        message: formData.message,
      });

      const response = await fetch(GOOGLE_SHEET_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody.toString(),
      });

      if (response.ok || response.status === 0) {
        setShowSuccess(true);
        setFormData({
          fname: "",
          email: "",
          phone: "",
          date: "",
          type: "",
          location: "",
          message: "",
        });

        setTimeout(() => {
          setShowSuccess(false);
          navigate("/");
        }, 2500);
      } else {
        alert("Submission failed.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error. Please try again later.");
    }
  };

  return (
    <section className="w-full flex flex-col items-center px-4 pt-10 relative">
      {showSuccess && (
        <div className="absolute top-0 left-0 w-full h-full bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl animate-fade-in-up text-center">
            <svg
              className="w-12 h-12 text-green-500 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-lg font-semibold text-gray-800">
              Submitted Successfully!
            </p>
          </div>
        </div>
      )}

      <p className="text-[#76cfff] font-poppins tracking-[4px] uppercase text-[16px] font-bold text-center mb-5">
        Book Your Event Now!
      </p>

      <h1 className="text-white font-poppins uppercase font-semibold text-[48px] leading-[60px] text-center mb-6 sm:text-[40px] sm:leading-[50px]">
        Contact Us
      </h1>

      {/*
        ✅ netlify attribute added — Netlify detects this form at build time.
        data-netlify="true" is the JSX-safe equivalent of the bare `netlify` attribute.
        The hidden input tells Netlify which form this submission belongs to.
      */}
      <form
        className="flex flex-col items-center w-full max-w-[600px]"
        onSubmit={handleSubmit}
        name="contact"
        data-netlify="true"
      >
        {/* ✅ Required by Netlify for AJAX submissions */}
        <input type="hidden" name="form-name" value="contact" />

        {[
          { label: "Full Name", id: "fname", type: "text", placeholder: "Kirolos Medhat" },
          { label: "Email", id: "email", type: "text", placeholder: "email@example.com" },
          { label: "Phone", id: "phone", type: "text", placeholder: "0123456789" },
          { label: "Event Date", id: "date", type: "date" },
          { label: "Event Type", id: "type", type: "text", placeholder: "Wedding, Birthday..." },
          { label: "Event Location", id: "location", type: "text", placeholder: "Location" },
          { label: "Message", id: "message", type: "text", placeholder: "Your Message" },
        ].map(({ label, id, type, placeholder, min, max }) => (
          <div className="w-full mb-4" key={id}>
            <label
              htmlFor={id}
              className="text-white uppercase font-poppins text-[16px] font-semibold mb-1 block ml-2"
            >
              {label}
            </label>
            <input
              id={id}
              name={id}
              type={type}
              placeholder={placeholder}
              min={min}
              max={max}
              value={formData[id]}
              onChange={handleChange}
              className={`w-full h-[50px] px-4 text-center rounded-full border-2 ${
                errors[id] ? "border-red-500" : "border-[#76cfff]"
              } bg-transparent text-white placeholder-white/60 font-poppins text-[16px] focus:outline-none`}
            />
            {errors[id] && (
              <p className="text-red-500 text-sm mt-1 ml-3">{errors[id]}</p>
            )}
          </div>
        ))}

        <button
          type="submit"
          className="mt-4 px-6 py-3 bg-[#76cfff] text-white rounded-full font-poppins text-[16px] hover:bg-white hover:text-[#76cfff] transition-all duration-500"
        >
          Send
        </button>
      </form>
    </section>
  );
};

export default Contact;